import {
  BUFFER_MAXIMO_EM_HORAS,
  DIAS_MAXIMOS_DE_AVISO,
  DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO,
  DIAS_MINIMOS_DE_AVISO,
  JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS,
  ROTULO_DO_TIPO_DE_TAXA_DE_MULTA,
  TIPOS_DE_TAXA_DE_MULTA,
  type TipoDeTaxaDeMulta,
  MOTIVOS_CONTROLADOS_PELA_RESERVA,
  dayOfWeekFromKey,
  MOTIVOS_DE_BLOQUEIO,
  type MotivoDeBloqueio,
  OVERBOOKING_MAXIMO_EM_PONTOS_BASE,
  PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS,
  ROTULO_DO_MOTIVO_DE_BLOQUEIO,
  ROTULO_DO_STATUS_DE_PRESTADOR,
  ROTULO_DO_TIPO_DE_PRESTADOR,
  salvarPoliticaDeLocacaoSchema,
  STATUS_DE_PRESTADOR,
  type StatusDoPrestador,
  TIPOS_DE_PRESTADOR,
  type TipoDePrestador,
  TOLERANCIA_MAXIMA_EM_MINUTOS,
  VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS,
} from '@locaveri/shared'
import type { ColumnDef } from '@tanstack/react-table'
import {
  CalendarDays,
  CalendarRange,
  CarFront,
  CircleCheck,
  CircleSlash,
  EllipsisVertical,
  LoaderCircle,
  LockOpen,
  type LucideIcon,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  SlidersHorizontal,
  Users,
} from 'lucide-react'
import { type ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

import { Paginacao } from '@/components/data/paginacao'
import { TabelaDeDados } from '@/components/data/tabela-de-dados'
import { Campo } from '@/components/form/campo'
import { EmptyState } from '@/components/state/empty-state'
import { ErrorState } from '@/components/state/error-state'
import { LoadingState } from '@/components/state/loading-state'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { identificacaoDoVeiculo, useCategoriasAtivas, useLocaisAtivos } from '@/features/frota/api'
import {
  formatarPontosBaseComoPercentual,
  ROTULO_DA_ORIGEM_DA_TOLERANCIA,
  rotuloDosDiasDaSemana,
  usePoliticaDeLocacao,
  useSalvarPoliticaDeLocacao,
  useSimulacaoDeDiarias,
} from '@/features/precos/api'
import { BadgeDeMotivoDeBloqueio, BadgeDeTipoDePrestador } from '@/features/precos/badges'
import {
  type BloqueioDePrestador,
  type BloqueioDeVeiculo,
  type CapacidadeDaCategoria,
  disponibilidadeEmTexto,
  type Prestador,
  useBloqueios,
  useCalendario,
  useCapacidade,
  useEscala,
  useInativarPrestador,
  usePrestadores,
  usePrestadoresLivres,
  useRemoverBloqueio,
  useRemoverBloqueioDePrestador,
} from '@/features/precos/cotacao'
import {
  type AlvoDoBloqueio,
  campoDeInstanteRelativo,
  DialogoDeBloqueio,
  diaRelativo,
  erroDoPeriodo,
  paraInstante,
} from '@/features/precos/dialogo-de-bloqueio'
import { DialogoDePrestador } from '@/features/precos/dialogo-de-prestador'
import { errorMessage } from '@/lib/api'
import { formatarData, formatarDataHora, SEM_VALOR } from '@/lib/datas'
import { deCampoDeReaisParaWire, formatarMoedaDoWire, paraCampoDeReais } from '@/lib/dinheiro'
import { aplicarErrosDaApi, aplicarErrosDoZod } from '@/lib/formulario'
import { usePermissao } from '@/lib/permissoes'

/**
 * Disponibilidade — o que a frota tem de pé, dia a dia.
 *
 * ---------------------------------------------------------------------------
 * DISPONIBILIDADE É POR CATEGORIA; OCUPAÇÃO É POR VEÍCULO
 * ---------------------------------------------------------------------------
 * A regra de ouro decide o desenho da tela. A pergunta comercial ("quantos SUV
 * blindados sobram de 10 a 14 de março?") é respondida por CATEGORIA, e é o
 * topo da página. O bloqueio — manutenção, limpeza, vistoria, sinistro — é por
 * PLACA, porque é um carro específico que está na oficina, e vive mais abaixo,
 * como entrada da operação. Inverter isso faria o atendimento procurar carro
 * antes de vender categoria.
 *
 * `capacidade` e `disponiveis` aparecem SEMPRE juntos ("2 de 8"): "2
 * disponíveis" esconde se a frota tem duas ou oitenta unidades, e é essa
 * diferença que decide entre vender e comprar carro.
 *
 * ---------------------------------------------------------------------------
 * O BUFFER PRECISA ESTAR ESCRITO NA TELA
 * ---------------------------------------------------------------------------
 * A janela consultada é dilatada pelo buffer de limpeza e vistoria nas duas
 * pontas, e a API devolve `janelaDe`/`janelaAte` justamente para a tela poder
 * explicar por que um carro que devolve às 10h não aparece livre para as 10h05.
 * Sem essa frase impressa, a operação abre chamado — foi para isso que os dois
 * campos vieram na resposta.
 *
 * ---------------------------------------------------------------------------
 * `ALOCACAO` E `RESERVA` SÓ SE OLHAM
 * ---------------------------------------------------------------------------
 * Esses dois motivos nascem da reserva (Fase 4). A tela os EXIBE — esconder
 * deixaria a operação sem entender por que o carro está ocupado — mas não
 * oferece editar nem liberar: a API recusa (422), e liberar por aqui não
 * cancelaria a reserva, só faria o sistema vender de novo um carro que já tem
 * dono.
 *
 * ---------------------------------------------------------------------------
 * A POLÍTICA VEM COM SIMULAÇÃO
 * ---------------------------------------------------------------------------
 * Tolerância, buffer e overbooking são três números que mudam dinheiro. O
 * prompt da fase é literal: "regra de preço sem simulação o gestor não confia".
 * Por isso o diálogo da política tem a pré-visualização ao lado do campo —
 * "devolvendo X minutos atrasado, o cliente paga N diárias" — e ela roda com o
 * valor que está sendo DIGITADO, antes de salvar.
 *
 * O amarelo aparece uma vez por tela: no CTA que bloqueia um veículo. Contagem,
 * esgotado e alerta são cinza, âmbar e vermelho semânticos — amarelo aqui é
 * marca, nunca aviso.
 */

const TAMANHO_DA_PAGINA = 20

const UM_DIA_EM_MS = 86_400_000

/** Dias corridos entre duas datas de calendário. `null` quando alguma não é data. */
function diasEntre(de: string, ate: string): number | null {
  const inicio = Date.parse(`${de}T12:00:00Z`)
  const fim = Date.parse(`${ate}T12:00:00Z`)

  if (Number.isNaN(inicio) || Number.isNaN(fim)) {
    return null
  }

  return Math.round((fim - inicio) / UM_DIA_EM_MS)
}

const FIM_DE_SEMANA = new Set([0, 6])

/**
 * O ícone de cada tipo de prestador.
 *
 * `Record` e não `tipo === 'MOTORISTA' ? … : …`: um terceiro tipo em
 * `TIPOS_DE_PRESTADOR` quebra o typecheck aqui, e não vira escudo de segurança
 * em cima do nome de um manobrista.
 */
const ICONE_DO_TIPO_DE_PRESTADOR: Readonly<Record<TipoDePrestador, LucideIcon>> = {
  MOTORISTA: CarFront,
  SEGURANCA: ShieldCheck,
}

/**
 * O nome da base do prestador, com as três leituras separadas.
 *
 * O mapa só tem base ATIVA. Um traço para o id que não está nele seria lido como
 * "não tem base" — que é exatamente o oposto: quem não tem base conta como livre
 * em TODAS elas, e quem está preso a uma base desativada não conta em nenhuma.
 * Confundir as duas coisas erra a contagem de prestadores livres na cotação.
 */
function nomeDaBase(locationId: string | null, bases: ReadonlyMap<string, string>): string {
  if (locationId === null) {
    return 'Qualquer base'
  }

  return bases.get(locationId) ?? 'Base desativada'
}

/**
 * A situação do prestador. Vive aqui, e não em `features/precos/badges`, porque
 * só esta tela a mostra — o selo compartilhado nasce quando o segundo consumidor
 * aparece.
 */
function BadgeDeSituacaoDoPrestador({
  status,
}: {
  readonly status: StatusDoPrestador
}): ReactElement {
  const ativo = status === 'ATIVO'
  const Icone = ativo ? CircleCheck : CircleSlash

  return (
    <Badge variant={ativo ? 'success' : 'neutral'}>
      <Icone aria-hidden="true" />
      {ROTULO_DO_STATUS_DE_PRESTADOR[status]}
    </Badge>
  )
}

// ---------------------------------------------------------------------------
// Política de locação
// ---------------------------------------------------------------------------

const CAMPOS_DA_POLITICA = [
  'toleranciaEmMinutos',
  'bufferEmHoras',
  'overbookingEmPontosBase',
  'precoPorOcupacaoAtivo',
  'validadeDaCotacaoEmMinutos',
  'prazoDePagamentoEmHoras',
  'reembolsoDeDevolucaoAntecipadaPercentual',
  'noShowAposHoras',
  'diasDeRetencaoDaCaucao',
  'cobrancaPosteriorDeMulta',
  'diasDeAvisoAntesDaCobranca',
  'taxaDeMultaTipo',
  'taxaDeMultaPercentual',
  'taxaDeMultaFixa',
] as const

interface FormularioDaPolitica {
  toleranciaEmMinutos: string
  bufferEmHoras: string
  overbookingEmPontosBase: string
  precoPorOcupacaoAtivo: boolean
  validadeDaCotacaoEmMinutos: string
  prazoDePagamentoEmHoras: string
  reembolsoDeDevolucaoAntecipadaPercentual: string
  /** `''` = desligado (no-show continua decisão manual do balcão). */
  noShowAposHoras: string
  diasDeRetencaoDaCaucao: string
  cobrancaPosteriorDeMulta: boolean
  diasDeAvisoAntesDaCobranca: string
  taxaDeMultaTipo: TipoDeTaxaDeMulta
  taxaDeMultaPercentual: string
  /** Em REAIS, como a pessoa digita ("15,00"); vira centavos no envio. */
  taxaDeMultaFixa: string
}

interface ValoresDaPolitica {
  readonly toleranciaEmMinutos: number
  readonly bufferEmHoras: number
  readonly overbookingEmPontosBase: number
  readonly precoPorOcupacaoAtivo: boolean
  readonly validadeDaCotacaoEmMinutos: number
  readonly prazoDePagamentoEmHoras: number
  readonly reembolsoDeDevolucaoAntecipadaPercentual: number
  readonly noShowAposHoras: number | null
  readonly diasDeRetencaoDaCaucao: number
  readonly cobrancaPosteriorDeMulta: boolean
  readonly diasDeAvisoAntesDaCobranca: number
  readonly taxaDeMultaTipo: TipoDeTaxaDeMulta
  readonly taxaDeMultaPercentual: number
  readonly taxaDeMultaFixa: string
}

function valoresIniciaisDaPolitica(politica: ValoresDaPolitica | undefined): FormularioDaPolitica {
  const noShow = politica?.noShowAposHoras ?? null

  return {
    toleranciaEmMinutos: String(politica?.toleranciaEmMinutos ?? 0),
    bufferEmHoras: String(politica?.bufferEmHoras ?? 3),
    overbookingEmPontosBase: String(politica?.overbookingEmPontosBase ?? 0),
    precoPorOcupacaoAtivo: politica?.precoPorOcupacaoAtivo ?? false,
    validadeDaCotacaoEmMinutos: String(politica?.validadeDaCotacaoEmMinutos ?? 30),
    prazoDePagamentoEmHoras: String(politica?.prazoDePagamentoEmHoras ?? 2),
    reembolsoDeDevolucaoAntecipadaPercentual: String(
      politica?.reembolsoDeDevolucaoAntecipadaPercentual ?? 0,
    ),
    noShowAposHoras: noShow === null ? '' : String(noShow),
    diasDeRetencaoDaCaucao: String(politica?.diasDeRetencaoDaCaucao ?? 0),
    cobrancaPosteriorDeMulta: politica?.cobrancaPosteriorDeMulta ?? false,
    diasDeAvisoAntesDaCobranca: String(politica?.diasDeAvisoAntesDaCobranca ?? 3),
    taxaDeMultaTipo: politica?.taxaDeMultaTipo ?? 'NENHUMA',
    taxaDeMultaPercentual: String(politica?.taxaDeMultaPercentual ?? 0),
    taxaDeMultaFixa: paraCampoDeReais(politica?.taxaDeMultaFixa ?? '0'),
  }
}

/** Inteiro digitado, ou `''` quando o campo ainda não é um número utilizável. */
function inteiroDoCampo(valor: string): number | '' {
  const numero = Number(valor)

  return valor.trim().length > 0 && Number.isInteger(numero) && numero >= 0 ? numero : ''
}

interface DialogoDePoliticaProps {
  readonly aberto: boolean
  readonly aoFechar: () => void
  readonly podeEditar: boolean
}

/**
 * A política de contagem de diária, com a pré-visualização ao lado.
 *
 * A simulação usa a tolerância DIGITADA (não a salva): o ponto da tela é testar
 * o número antes de assumi-lo. E o exemplo é editável porque "20 minutos de
 * atraso numa diária" e "20 minutos numa quinzena" caem em faixas diferentes —
 * o gestor precisa poder perguntar pelo caso dele.
 */
function DialogoDePoliticaDeLocacao({
  aberto,
  aoFechar,
  podeEditar,
}: DialogoDePoliticaProps): ReactElement {
  const [erroGeral, setErroGeral] = useState<string | null>(null)
  const [retiradaDoExemplo, setRetiradaDoExemplo] = useState(() =>
    campoDeInstanteRelativo(0, '09:00'),
  )
  const [devolucaoDoExemplo, setDevolucaoDoExemplo] = useState(() =>
    campoDeInstanteRelativo(3, '09:00'),
  )
  const [atrasoDigitado, setAtrasoDigitado] = useState('20')

  const consulta = usePoliticaDeLocacao()
  const salvar = useSalvarPoliticaDeLocacao()

  const {
    register,
    handleSubmit,
    setError,
    reset,
    watch,
    formState: { errors },
  } = useForm<FormularioDaPolitica>({ defaultValues: valoresIniciaisDaPolitica(undefined) })

  // O diálogo é montado uma vez e reaproveitado: sem este efeito, reabrir depois
  // de um cancelamento mostraria o que a pessoa tinha digitado e desistido.
  useEffect(() => {
    if (aberto) {
      setErroGeral(null)
      reset(valoresIniciaisDaPolitica(consulta.data))
    }
  }, [aberto, consulta.data, reset])

  const toleranciaDigitada = watch('toleranciaEmMinutos')
  const tipoDeTaxa = watch('taxaDeMultaTipo')
  const cobrancaPosterior = watch('cobrancaPosteriorDeMulta')
  const ocupado = salvar.isPending

  const atraso = inteiroDoCampo(atrasoDigitado)
  const retiradaIso = paraInstante(retiradaDoExemplo)
  const devolucaoPrevistaIso = paraInstante(devolucaoDoExemplo)

  /**
   * A devolução REAL do exemplo: a prevista mais o atraso. Somar minutos a um
   * instante é aritmética de tempo, não de dinheiro — e o resultado volta em
   * UTC, que é uma das duas formas que a API aceita.
   */
  const devolucaoComAtraso = useMemo(() => {
    if (devolucaoPrevistaIso === null || atraso === '') {
      return ''
    }

    const instante = new Date(devolucaoPrevistaIso).getTime()

    if (Number.isNaN(instante)) {
      return ''
    }

    return new Date(instante + atraso * 60_000).toISOString()
  }, [devolucaoPrevistaIso, atraso])

  const erroDoExemplo = erroDoPeriodo(retiradaIso ?? '', devolucaoComAtraso)

  /**
   * O exemplo só está pronto com as duas pontas preenchidas E válidas.
   *
   * A consulta guarda o resultado anterior (`placeholderData`), e sem esta
   * porta o diálogo continuaria exibindo a contagem do período antigo depois
   * que a pessoa apagasse uma data — um número certo respondendo a uma
   * pergunta que não está mais na tela.
   */
  const exemploPronto =
    erroDoExemplo === null && retiradaIso !== null && devolucaoComAtraso.length > 0

  const simulacao = useSimulacaoDeDiarias({
    retirada: erroDoExemplo === null ? (retiradaIso ?? '') : '',
    devolucao: erroDoExemplo === null ? devolucaoComAtraso : '',
    toleranciaEmMinutos: inteiroDoCampo(toleranciaDigitada),
    categoryId: '',
  })

  const aoEnviar = handleSubmit((valores) => {
    setErroGeral(null)

    // Campo vazio = no-show desligado. O schema shared não sabe disso — coerção
    // de `''` daria 0, que reprova no `positive()` — então o `null` entra aqui.
    // A taxa fixa é digitada em reais e viaja em centavos; texto que não é
    // dinheiro vira o valor inválido que o schema recusa com a mensagem certa.
    const taxaFixaEmCentavos =
      valores.taxaDeMultaTipo === 'FIXA'
        ? (deCampoDeReaisParaWire(valores.taxaDeMultaFixa) ?? 'invalido')
        : '0'
    const validado = salvarPoliticaDeLocacaoSchema.safeParse({
      ...valores,
      noShowAposHoras: valores.noShowAposHoras.trim() === '' ? null : valores.noShowAposHoras,
      taxaDeMultaFixa: taxaFixaEmCentavos,
    })

    if (!validado.success) {
      setErroGeral(aplicarErrosDoZod(validado.error, setError, CAMPOS_DA_POLITICA))
      return
    }

    // Aqui vale `validado.data`, ao contrário dos formulários de dinheiro: este
    // schema só COAGE string para inteiro e booleano — nada de `bigint` nem de
    // `Date`, que são os dois tipos que `JSON.stringify` não sabe serializar.
    salvar.mutate(validado.data, {
      onSuccess: () => {
        toast.success('Política salva', {
          description: 'Vale das próximas cotações em diante — as já emitidas não mudam.',
        })
        aoFechar()
      },
      onError: (causa: unknown) => {
        setErroGeral(aplicarErrosDaApi(causa, setError, CAMPOS_DA_POLITICA))
      },
    })
  })

  const resultado = simulacao.data

  return (
    <Dialog
      open={aberto}
      onOpenChange={(proximo) => {
        if (!proximo) {
          aoFechar()
        }
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Política de locação</DialogTitle>
          <DialogDescription>
            Tolerância de devolução, buffer entre locações, overbooking, validade da cotação, prazo
            de pagamento, reembolso antecipado e no-show automático. Uma por locadora.
          </DialogDescription>
        </DialogHeader>

        {consulta.isPending ? (
          <LoadingState rotulo="Carregando a política…" linhas={2} />
        ) : consulta.isError ? (
          <ErrorState
            titulo="Não foi possível carregar a política"
            erro={consulta.error}
            aoTentarNovamente={() => {
              void consulta.refetch()
            }}
          />
        ) : (
          <form
            noValidate
            onSubmit={(evento) => {
              void aoEnviar(evento)
            }}
            className="space-y-4"
          >
            {erroGeral === null ? null : (
              <Alert variant="danger">
                <AlertDescription>{erroGeral}</AlertDescription>
              </Alert>
            )}

            {consulta.data.definida ? null : (
              <Alert variant="warning">
                <AlertTitle>Ninguém salvou esta política ainda</AlertTitle>
                <AlertDescription>
                  Os números abaixo são os padrões de fábrica, não uma decisão da casa. Enquanto
                  ninguém salvar, é isso que a cotação usa.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo
                rotulo="Tolerância de devolução"
                obrigatorio
                erro={errors.toleranciaEmMinutos?.message}
                dica={`Em minutos, de 0 a ${String(TOLERANCIA_MAXIMA_EM_MINUTOS)}. Zero é o padrão do setor: qualquer sobra cobra diária.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('toleranciaEmMinutos')}
                    type="number"
                    min={0}
                    max={TOLERANCIA_MAXIMA_EM_MINUTOS}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Buffer entre locações"
                obrigatorio
                erro={errors.bufferEmHoras?.message}
                dica={`Em horas, até ${String(BUFFER_MAXIMO_EM_HORAS)}. É o tempo de limpeza e vistoria — ele dilata a janela nas duas pontas.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('bufferEmHoras')}
                    type="number"
                    min={0}
                    max={BUFFER_MAXIMO_EM_HORAS}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Overbooking"
                obrigatorio
                erro={errors.overbookingEmPontosBase?.message}
                dica={`Em pontos-base sobre a capacidade: 100 pontos-base = 1%, então 1500 = 15%. Vai até ${String(OVERBOOKING_MAXIMO_EM_PONTOS_BASE)} (${formatarPontosBaseComoPercentual(OVERBOOKING_MAXIMO_EM_PONTOS_BASE)}). Zero desliga.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('overbookingEmPontosBase')}
                    type="number"
                    min={0}
                    max={OVERBOOKING_MAXIMO_EM_PONTOS_BASE}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Validade da cotação"
                obrigatorio
                erro={errors.validadeDaCotacaoEmMinutos?.message}
                dica={`Em minutos, até ${String(VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS)}. Preço sem prazo vira preço eterno.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('validadeDaCotacaoEmMinutos')}
                    type="number"
                    min={1}
                    max={VALIDADE_MAXIMA_DA_COTACAO_EM_MINUTOS}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Prazo para pagar após envio para pagamento (horas)"
                obrigatorio
                erro={errors.prazoDePagamentoEmHoras?.message}
                dica={`Até ${String(PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS)} horas. Vencido o prazo, a reserva expira sozinha.`}
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('prazoDePagamentoEmHoras')}
                    type="number"
                    min={1}
                    max={PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Reembolso de devolução antecipada (%)"
                obrigatorio
                erro={errors.reembolsoDeDevolucaoAntecipadaPercentual?.message}
                dica="Percentual do valor das diárias NÃO usadas creditado ao cliente no fechamento. Zero = sem reembolso (padrão do setor)."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('reembolsoDeDevolucaoAntecipadaPercentual')}
                    type="number"
                    min={0}
                    max={100}
                    inputMode="numeric"
                    className="tabular-money"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>

              <Campo
                rotulo="Marcar não comparecimento após (horas)"
                erro={errors.noShowAposHoras?.message}
                dica="Vazio, marcar no-show continua decisão manual do balcão. Preenchido, o sistema marca sozinho após esse atraso na retirada, com o motivo registrado na trilha."
              >
                {(campo) => (
                  <Input
                    {...campo}
                    {...register('noShowAposHoras')}
                    type="number"
                    min={1}
                    max={PRAZO_MAXIMO_DE_PAGAMENTO_EM_HORAS}
                    inputMode="numeric"
                    className="tabular-money"
                    placeholder="Vazio = decisão manual"
                    disabled={ocupado || !podeEditar}
                  />
                )}
              </Campo>
            </div>

            {/* ------------------------------------------------------------
                Multa que chega depois da devolução (decisão de 26/10/2026)
                ------------------------------------------------------------ */}
            <fieldset className="space-y-3 rounded-lg border border-border bg-surface p-4">
              <legend className="px-1 text-sm font-medium text-foreground">
                Multas que chegam depois da devolução
              </legend>
              <p className="text-xs text-fg-secondary">
                Vale para as reservas criadas a partir de agora — a regra fica congelada na reserva
                e entra no contrato como cláusula.
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo
                  rotulo="Segurar a caução por (dias)"
                  obrigatorio
                  erro={errors.diasDeRetencaoDaCaucao?.message}
                  dica={`Depois da devolução, a caução continua bloqueada no cartão e a multa que chegar nesse prazo sai dela. Zero = libera no fechamento. Máximo ${String(DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO)}: o bloqueio vence sozinho cerca de 28 dias depois de feito.`}
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('diasDeRetencaoDaCaucao')}
                      type="number"
                      min={0}
                      max={DIAS_MAXIMOS_DE_RETENCAO_DA_CAUCAO}
                      inputMode="numeric"
                      className="tabular-money"
                      disabled={ocupado || !podeEditar}
                    />
                  )}
                </Campo>

                <Campo
                  rotulo="Tipo de taxa de administração"
                  obrigatorio
                  erro={errors.taxaDeMultaTipo?.message}
                  dica="Cobrada sobre cada multa repassada, em linha separada do valor do auto."
                >
                  {(campo) => (
                    <Select
                      {...campo}
                      {...register('taxaDeMultaTipo')}
                      disabled={ocupado || !podeEditar}
                    >
                      {TIPOS_DE_TAXA_DE_MULTA.map((tipo) => (
                        <option key={tipo} value={tipo}>
                          {ROTULO_DO_TIPO_DE_TAXA_DE_MULTA[tipo]}
                        </option>
                      ))}
                    </Select>
                  )}
                </Campo>

                {tipoDeTaxa === 'PERCENTUAL' ? (
                  <Campo
                    rotulo="Taxa sobre a multa (%)"
                    obrigatorio
                    erro={errors.taxaDeMultaPercentual?.message}
                  >
                    {(campo) => (
                      <Input
                        {...campo}
                        {...register('taxaDeMultaPercentual')}
                        type="number"
                        min={0}
                        max={100}
                        inputMode="numeric"
                        className="tabular-money"
                        disabled={ocupado || !podeEditar}
                      />
                    )}
                  </Campo>
                ) : null}

                {tipoDeTaxa === 'FIXA' ? (
                  <Campo
                    rotulo="Taxa por multa (R$)"
                    obrigatorio
                    erro={errors.taxaDeMultaFixa?.message}
                  >
                    {(campo) => (
                      <Input
                        {...campo}
                        {...register('taxaDeMultaFixa')}
                        inputMode="decimal"
                        placeholder="0,00"
                        className="tabular-money"
                        disabled={ocupado || !podeEditar}
                      />
                    )}
                  </Campo>
                ) : null}
              </div>

              <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border bg-background p-3 text-sm">
                <Checkbox
                  {...register('cobrancaPosteriorDeMulta')}
                  className="mt-0.5"
                  disabled={ocupado || !podeEditar}
                />
                <span className="min-w-0">
                  <span className="block font-medium text-foreground">
                    Cobrar a multa depois, no cartão do cliente
                  </span>
                  <span className="block text-xs text-fg-secondary">
                    O cartão fica guardado no provedor de pagamento (Cielo), nunca no Locaveri. O
                    cliente aceita no contrato e é avisado antes de cada cobrança.
                  </span>
                </span>
              </label>

              {cobrancaPosterior ? (
                <Campo
                  rotulo="Avisar o cliente quantos dias antes de cobrar"
                  obrigatorio
                  erro={errors.diasDeAvisoAntesDaCobranca?.message}
                  dica={`Entre ${String(DIAS_MINIMOS_DE_AVISO)} e ${String(DIAS_MAXIMOS_DE_AVISO)} dias. O aviso sai por e-mail e no app; a cobrança vem depois.`}
                >
                  {(campo) => (
                    <Input
                      {...campo}
                      {...register('diasDeAvisoAntesDaCobranca')}
                      type="number"
                      min={DIAS_MINIMOS_DE_AVISO}
                      max={DIAS_MAXIMOS_DE_AVISO}
                      inputMode="numeric"
                      className="tabular-money"
                      disabled={ocupado || !podeEditar}
                    />
                  )}
                </Campo>
              ) : null}
            </fieldset>

            <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-border bg-surface p-3 text-sm">
              <Checkbox
                {...register('precoPorOcupacaoAtivo')}
                className="mt-0.5"
                disabled={ocupado || !podeEditar}
              />
              <span className="min-w-0">
                <span className="block font-medium text-foreground">Preço por ocupação</span>
                <span className="block text-xs text-fg-secondary">
                  Desligado, a quinta camada de regra nem é consultada. Ligado, a diária sobe
                  conforme a ocupação prevista da categoria no período.
                </span>
              </span>
            </label>

            {/* ------------------------------------------------------------
                Pré-visualização — o que a tolerância faz com a conta
                ------------------------------------------------------------ */}
            <div className="space-y-3 rounded-lg border border-border bg-surface p-4">
              <div>
                <h3 className="text-sm font-medium text-foreground">
                  Devolvendo atrasado, quantas diárias?
                </h3>
                <p className="mt-1 text-xs text-fg-secondary">
                  A simulação usa a tolerância que você está digitando, não a que está salva.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <Label htmlFor="exemplo-retirada">Retirada</Label>
                  <Input
                    id="exemplo-retirada"
                    type="datetime-local"
                    value={retiradaDoExemplo}
                    onChange={(evento) => {
                      setRetiradaDoExemplo(evento.target.value)
                    }}
                    className="tabular-money"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="exemplo-devolucao">Devolução prevista</Label>
                  <Input
                    id="exemplo-devolucao"
                    type="datetime-local"
                    value={devolucaoDoExemplo}
                    onChange={(evento) => {
                      setDevolucaoDoExemplo(evento.target.value)
                    }}
                    className="tabular-money"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="exemplo-atraso">Atraso (min)</Label>
                  <Input
                    id="exemplo-atraso"
                    type="number"
                    min={0}
                    inputMode="numeric"
                    value={atrasoDigitado}
                    onChange={(evento) => {
                      setAtrasoDigitado(evento.target.value)
                    }}
                    className="tabular-money"
                  />
                </div>
              </div>

              {erroDoExemplo === null ? null : (
                <Alert variant="warning">
                  <AlertDescription>{erroDoExemplo}</AlertDescription>
                </Alert>
              )}

              {!exemploPronto ? (
                <p className="text-xs text-fg-muted">
                  Preencha um período válido no exemplo para ver a contagem.
                </p>
              ) : simulacao.isPending ? (
                <Skeleton className="h-16 w-full" />
              ) : simulacao.isError ? (
                <Alert variant="danger">
                  <AlertTitle>Não foi possível simular</AlertTitle>
                  <AlertDescription>{errorMessage(simulacao.error)}</AlertDescription>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="mt-2"
                    onClick={() => {
                      void simulacao.refetch()
                    }}
                  >
                    <RefreshCw aria-hidden="true" />
                    Tentar de novo
                  </Button>
                </Alert>
              ) : resultado === undefined ? (
                <p className="text-xs text-fg-muted">
                  Preencha um período válido no exemplo para ver a contagem.
                </p>
              ) : (
                <div className="space-y-2">
                  {simulacao.isFetching ? (
                    <p role="status" className="text-xs text-fg-muted">
                      Recalculando com o que você acabou de digitar…
                    </p>
                  ) : null}

                  <p className="text-sm text-foreground">
                    Com{' '}
                    <span className="tabular-money font-medium">
                      {resultado.toleranciaEmMinutos} min
                    </span>{' '}
                    de tolerância, devolvendo{' '}
                    <span className="tabular-money font-medium">
                      {atraso === '' ? 0 : atraso} min
                    </span>{' '}
                    atrasado o cliente paga{' '}
                    <span className="numeric-display text-brand">{resultado.diarias}</span>{' '}
                    {resultado.diarias === 1 ? 'diária' : 'diárias'}.
                  </p>

                  <dl className="grid gap-2 text-xs sm:grid-cols-3">
                    <div>
                      <dt className="text-fg-muted">Sem tolerância nenhuma</dt>
                      <dd className="tabular-money text-foreground">
                        {resultado.diariasSemTolerancia}{' '}
                        {resultado.diariasSemTolerancia === 1 ? 'diária' : 'diárias'}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-fg-muted">Faixa da tarifa</dt>
                      <dd className="text-foreground">{resultado.faixaRotulo}</dd>
                    </div>
                    <div>
                      <dt className="text-fg-muted">De onde veio a tolerância</dt>
                      <dd className="text-foreground">
                        {ROTULO_DA_ORIGEM_DA_TOLERANCIA[resultado.origemDaTolerancia]}
                      </dd>
                    </div>
                  </dl>

                  {resultado.diariaExtraEvitada ? (
                    <Alert variant="success">
                      <AlertDescription>
                        A tolerância evitou uma diária extra neste atraso.
                      </AlertDescription>
                    </Alert>
                  ) : resultado.toleranciaMinimaParaEvitarADiaria === null ? (
                    <Alert variant="neutral">
                      <AlertDescription>
                        Nenhuma tolerância dentro do teto de{' '}
                        <span className="tabular-money">
                          {String(TOLERANCIA_MAXIMA_EM_MINUTOS)} min
                        </span>{' '}
                        evitaria a diária extra deste atraso — um dia inteiro de cortesia daria uma
                        diária de graça, e é por isso que o teto existe.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <Alert variant="warning">
                      <AlertDescription>
                        Este atraso ainda cobra a diária extra. Ela só não seria cobrada com uma
                        tolerância de pelo menos{' '}
                        <span className="tabular-money">
                          {resultado.toleranciaMinimaParaEvitarADiaria} min
                        </span>
                        .
                      </AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </div>

            <Alert variant="info">
              <AlertTitle>Mudar a política não mexe no que já foi cotado</AlertTitle>
              <AlertDescription>
                Cada cotação emitida congelou a tolerância que valia no momento, e é com ela que o
                faturamento vai contar as diárias.
              </AlertDescription>
            </Alert>

            <DialogFooter>
              <Button type="button" variant="secondary" onClick={aoFechar} disabled={ocupado}>
                {podeEditar ? 'Cancelar' : 'Fechar'}
              </Button>

              {podeEditar ? (
                <Button type="submit" disabled={ocupado}>
                  {ocupado ? <LoaderCircle className="animate-spin" aria-hidden="true" /> : null}
                  Salvar política
                </Button>
              ) : null}
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

/**
 * A política em uma linha, no rodapé da tela.
 *
 * Componente próprio para que a consulta só aconteça quando a linha existe: quem
 * não tem `rental_policy.read` não a vê, e não gasta um 403 para descobrir. Ela
 * divide a mesma chave de cache com o diálogo — uma requisição para os dois.
 */
function ResumoDaPolitica(): ReactElement {
  const consulta = usePoliticaDeLocacao()

  if (consulta.isPending) {
    return <Skeleton className="h-4 w-2/3" />
  }

  // Erro aqui não trava a tela: a linha é resumo, e a cotação continua usando a
  // política salva no servidor. Some sem explicação é que não pode.
  if (consulta.isError) {
    return (
      <p className="text-xs text-fg-muted">
        Não foi possível ler a política de locação ({errorMessage(consulta.error)}). Abra
        &quot;Política de locação&quot; para tentar de novo.
      </p>
    )
  }

  const politica = consulta.data

  return (
    <p className="text-xs tabular-money text-fg-muted">
      Política atual: tolerância de {politica.toleranciaEmMinutos} min, buffer de{' '}
      {politica.bufferEmHoras} h, overbooking de{' '}
      {formatarPontosBaseComoPercentual(politica.overbookingEmPontosBase)}, cotação válida por{' '}
      {politica.validadeDaCotacaoEmMinutos} min, {politica.prazoDePagamentoEmHoras} h para pagar,
      reembolso antecipado de {politica.reembolsoDeDevolucaoAntecipadaPercentual}% e no-show{' '}
      {politica.noShowAposHoras === null
        ? 'manual'
        : `automático após ${String(politica.noShowAposHoras)} h`}
      . Multas depois da devolução:{' '}
      {politica.diasDeRetencaoDaCaucao > 0
        ? `caução retida por ${String(politica.diasDeRetencaoDaCaucao)} dia(s)`
        : 'caução liberada no fechamento'}
      {politica.cobrancaPosteriorDeMulta
        ? `, cobrança no cartão com ${String(politica.diasDeAvisoAntesDaCobranca)} dia(s) de aviso`
        : ''}
      {politica.taxaDeMultaTipo === 'PERCENTUAL'
        ? `, taxa de ${String(politica.taxaDeMultaPercentual)}%`
        : politica.taxaDeMultaTipo === 'FIXA'
          ? `, taxa de ${formatarMoedaDoWire(politica.taxaDeMultaFixa)}`
          : ', sem taxa'}
      .{politica.definida ? '' : ' (padrões de fábrica — ninguém salvou ainda)'}
    </p>
  )
}

// ---------------------------------------------------------------------------
// Calendário
// ---------------------------------------------------------------------------

interface DiaDoCalendarioProps {
  readonly data: string
  readonly capacidade: number
  readonly disponiveis: number
}

/**
 * Um dia do calendário.
 *
 * Esgotado nunca é só a cor: o cartão ganha borda vermelha E a palavra
 * "esgotado". Fim de semana ganha um fundo mais claro porque é o padrão que o
 * gestor procura primeiro nesta grade — e não porque signifique algo de ruim.
 */
function CartaoDoDia({ data, capacidade, disponiveis }: DiaDoCalendarioProps): ReactElement {
  const esgotado = disponiveis === 0
  const ultimas = !esgotado && capacidade > 0 && disponiveis === 1
  const fimDeSemana = FIM_DE_SEMANA.has(dayOfWeekFromKey(data))

  return (
    <li
      className={
        esgotado
          ? 'rounded-lg border border-danger-fg/40 bg-danger/10 p-2'
          : fimDeSemana
            ? 'rounded-lg border border-border bg-elevated p-2'
            : 'rounded-lg border border-border bg-surface p-2'
      }
    >
      <p className="truncate text-xs tabular-money text-fg-muted">
        {rotuloDosDiasDaSemana([dayOfWeekFromKey(data)])} {formatarData(data).slice(0, 5)}
      </p>
      <p className="numeric-display text-lg text-foreground">{disponiveis}</p>
      <p className="text-xs tabular-money text-fg-secondary">de {capacidade}</p>
      {esgotado ? (
        <p className="mt-1 text-xs font-medium text-danger-fg">Esgotado</p>
      ) : ultimas ? (
        <p className="mt-1 text-xs font-medium text-warning-fg">Última unidade</p>
      ) : null}
    </li>
  )
}

// ---------------------------------------------------------------------------
// Tela
// ---------------------------------------------------------------------------

export function DisponibilidadePage(): ReactElement {
  const podeBloquear = usePermissao('availability.update')
  const podeVerPrestadores = usePermissao('service_provider.read')
  const podeCriarPrestador = usePermissao('service_provider.create')
  const podeEditarPrestador = usePermissao('service_provider.update')
  // Chave de CAMPO, não de rota: quem chega aqui por `availability.read` recebe
  // `service_provider.read` no mesmo pacote do seed, mas o custo da diária é
  // financeiro e o OPERACIONAL/ATENDIMENTO não o têm.
  const podeVerCusto = usePermissao('service_provider.cost')
  const podeInativarPrestador = usePermissao('service_provider.delete')
  const podeVerPolitica = usePermissao('rental_policy.read')
  const podeEditarPolitica = usePermissao('rental_policy.update')

  // A janela que a operação está olhando — capacidade e prestadores livres
  // respondem à MESMA pergunta, e separá-las em dois formulários faria a pessoa
  // digitar o período duas vezes.
  const [retiradaDigitada, setRetiradaDigitada] = useState(() =>
    campoDeInstanteRelativo(1, '09:00'),
  )
  const [devolucaoDigitada, setDevolucaoDigitada] = useState(() =>
    campoDeInstanteRelativo(4, '09:00'),
  )
  const [categoriaDaJanela, setCategoriaDaJanela] = useState('')
  const [localDaJanela, setLocalDaJanela] = useState('')

  const [categoriaDoCalendario, setCategoriaDoCalendario] = useState('')
  const [deDoCalendario, setDeDoCalendario] = useState(() => diaRelativo(0))
  const [ateDoCalendario, setAteDoCalendario] = useState(() => diaRelativo(30))

  const [paginaDeBloqueios, setPaginaDeBloqueios] = useState(1)
  const [categoriaDoBloqueio, setCategoriaDoBloqueio] = useState('')
  const [motivoDoBloqueio, setMotivoDoBloqueio] = useState<MotivoDeBloqueio | ''>('')
  const [deDoBloqueio, setDeDoBloqueio] = useState('')
  const [ateDoBloqueio, setAteDoBloqueio] = useState('')

  const [paginaDePrestadores, setPaginaDePrestadores] = useState(1)
  const [tipoDoPrestador, setTipoDoPrestador] = useState<TipoDePrestador | ''>('')
  const [situacaoDoPrestador, setSituacaoDoPrestador] = useState<StatusDoPrestador | ''>('')

  const [paginaDaEscala, setPaginaDaEscala] = useState(1)
  const [prestadorDaEscala, setPrestadorDaEscala] = useState('')

  const [bloqueioAberto, setBloqueioAberto] = useState(false)
  const [alvoDoDialogo, setAlvoDoDialogo] = useState<AlvoDoBloqueio>('VEICULO')
  const [bloqueioEmEdicao, setBloqueioEmEdicao] = useState<BloqueioDeVeiculo | undefined>(undefined)
  const [prestadorAberto, setPrestadorAberto] = useState(false)
  const [prestadorEmEdicao, setPrestadorEmEdicao] = useState<Prestador | undefined>(undefined)
  const [politicaAberta, setPoliticaAberta] = useState(false)

  // Clicar em "escala" de um prestador muda uma seção que, em 375px, está duas
  // telas abaixo — sem levar a página até lá, o clique não parece ter feito nada.
  const secaoDaEscala = useRef<HTMLElement>(null)

  const categorias = useCategoriasAtivas()
  const locais = useLocaisAtivos()
  const retiradaIso = paraInstante(retiradaDigitada)
  const devolucaoIso = paraInstante(devolucaoDigitada)
  const erroDaJanela = erroDoPeriodo(retiradaIso ?? '', devolucaoIso ?? '')
  const janelaValida = erroDaJanela === null && retiradaIso !== null && devolucaoIso !== null

  /**
   * `erroDoPeriodo` devolve `null` com uma ponta vazia — é "não sei ainda", e
   * não "está certo". Sem esta frase, apagar a retirada deixaria a tela MUDA
   * com a contagem do período anterior ainda impressa (as consultas guardam o
   * resultado anterior em `placeholderData`), e ninguém saberia que o número na
   * tela responde a outra pergunta.
   */
  const avisoDaJanela =
    retiradaIso === null || devolucaoIso === null
      ? 'informe a retirada e a devolução para consultar a disponibilidade'
      : erroDaJanela
  const retiradaDaJanela = janelaValida ? retiradaIso : ''
  const devolucaoDaJanela = janelaValida ? devolucaoIso : ''

  const parametrosDaJanela = useMemo(
    () => ({
      retirada: retiradaDaJanela,
      devolucao: devolucaoDaJanela,
      categoryId: categoriaDaJanela,
      retiradaLocalId: localDaJanela,
      devolucaoLocalId: localDaJanela,
    }),
    [retiradaDaJanela, devolucaoDaJanela, categoriaDaJanela, localDaJanela],
  )

  const capacidade = useCapacidade(parametrosDaJanela)

  const prestadoresLivres = usePrestadoresLivres(
    useMemo(
      () => ({
        retirada: parametrosDaJanela.retirada,
        devolucao: parametrosDaJanela.devolucao,
        tipo: '' as const,
        locationId: localDaJanela,
      }),
      [parametrosDaJanela, localDaJanela],
    ),
  )

  const diasDoCalendario = diasEntre(deDoCalendario, ateDoCalendario)

  // Data em branco desliga a consulta, e query desligada guarda o resultado
  // anterior: sem este ramo, apagar uma data deixaria o calendário do mês
  // passado na tela como se fosse a resposta da pergunta atual.
  const erroDoCalendario =
    deDoCalendario.length === 0 || ateDoCalendario.length === 0
      ? 'informe as duas datas do calendário'
      : diasDoCalendario === null
        ? 'as datas do calendário precisam estar completas'
        : diasDoCalendario < 0
          ? 'a data final precisa ser igual ou posterior à inicial'
          : diasDoCalendario > JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS
            ? `o calendário mostra até ${String(JANELA_MAXIMA_DO_CALENDARIO_EM_DIAS)} dias por vez`
            : null

  const calendario = useCalendario(
    useMemo(
      () => ({
        categoryId: categoriaDoCalendario,
        de: erroDoCalendario === null ? deDoCalendario : '',
        ate: erroDoCalendario === null ? ateDoCalendario : '',
        locationId: localDaJanela,
      }),
      [categoriaDoCalendario, erroDoCalendario, deDoCalendario, ateDoCalendario, localDaJanela],
    ),
  )

  const filtrosDeBloqueios = useMemo(
    () => ({
      pagina: paginaDeBloqueios,
      tamanho: TAMANHO_DA_PAGINA,
      vehicleId: '',
      serviceProviderId: '',
      categoryId: categoriaDoBloqueio,
      motivo: motivoDoBloqueio,
      // O filtro é de INSTANTE: o dia escolhido vira o dia inteiro, senão
      // "de 15/03" não pegaria o bloqueio que começa às 14h do dia 15.
      de: deDoBloqueio === '' ? '' : (paraInstante(`${deDoBloqueio}T00:00`) ?? ''),
      ate: ateDoBloqueio === '' ? '' : (paraInstante(`${ateDoBloqueio}T23:59`) ?? ''),
    }),
    [paginaDeBloqueios, categoriaDoBloqueio, motivoDoBloqueio, deDoBloqueio, ateDoBloqueio],
  )

  const bloqueios = useBloqueios(filtrosDeBloqueios)

  /**
   * As três consultas de prestador rodam sem condicional: todo papel que chega a
   * esta tela (`availability.read`) recebe `service_provider.read` no mesmo
   * pacote do seed. Um papel sob medida sem ela recebe 403 aqui, e a tela apenas
   * deixa de mostrar a seção — nenhum estado quebrado, e nada mais depende
   * delas. Já a política é o caso contrário (ATENDIMENTO e OPERACIONAL não a
   * leem), e por isso vive num componente que só é montado com permissão.
   */
  const filtrosDePrestadores = useMemo(
    () => ({
      pagina: paginaDePrestadores,
      tamanho: TAMANHO_DA_PAGINA,
      tipo: tipoDoPrestador,
      status: situacaoDoPrestador,
      locationId: '',
      livreDe: '',
      livreAte: '',
    }),
    [paginaDePrestadores, tipoDoPrestador, situacaoDoPrestador],
  )

  const prestadores = usePrestadores(filtrosDePrestadores)

  const filtrosDaEscala = useMemo(
    () => ({
      pagina: paginaDaEscala,
      tamanho: TAMANHO_DA_PAGINA,
      vehicleId: '',
      serviceProviderId: prestadorDaEscala,
      categoryId: '',
      motivo: '' as const,
      de: '',
      ate: '',
    }),
    [paginaDaEscala, prestadorDaEscala],
  )

  const escala = useEscala(filtrosDaEscala)

  const removerBloqueio = useRemoverBloqueio()
  const removerTurno = useRemoverBloqueioDePrestador()
  const inativarPrestador = useInativarPrestador()

  const listaDeCategorias = categorias.data?.itens ?? []

  // Referência estável: `?? []` cria um array novo a cada render, e ele é
  // dependência do mapa de bases — sem memoizar, o mapa se refaz sempre.
  const listaDeLocais = useMemo(() => locais.data?.itens ?? [], [locais.data])

  const bases = useMemo(
    () => new Map(listaDeLocais.map((local) => [local.id, local.nome])),
    [listaDeLocais],
  )

  const temFiltroDeBloqueio =
    categoriaDoBloqueio !== '' ||
    motivoDoBloqueio !== '' ||
    deDoBloqueio !== '' ||
    ateDoBloqueio !== ''

  const temFiltroDePrestador = tipoDoPrestador !== '' || situacaoDoPrestador !== ''

  function abrirBloqueioDeVeiculo(): void {
    setAlvoDoDialogo('VEICULO')
    setBloqueioEmEdicao(undefined)
    setBloqueioAberto(true)
  }

  function abrirEdicaoDeBloqueio(bloqueio: BloqueioDeVeiculo): void {
    setAlvoDoDialogo('VEICULO')
    setBloqueioEmEdicao(bloqueio)
    setBloqueioAberto(true)
  }

  function abrirTurnoDePrestador(): void {
    setAlvoDoDialogo('PRESTADOR')
    setBloqueioEmEdicao(undefined)
    setBloqueioAberto(true)
  }

  function liberarVeiculo(bloqueio: BloqueioDeVeiculo): void {
    removerBloqueio.mutate(bloqueio.id, {
      onSuccess: () => {
        toast.success(`${identificacaoDoVeiculo(bloqueio.veiculo)} liberado`, {
          description: 'A unidade voltou para a contagem de disponíveis da categoria.',
        })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível liberar o veículo', { description: errorMessage(causa) })
      },
    })
  }

  function liberarTurno(turno: BloqueioDePrestador): void {
    removerTurno.mutate(turno.id, {
      onSuccess: () => {
        toast.success(`${turno.prestador.nome} liberado no período`)
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível liberar o turno', { description: errorMessage(causa) })
      },
    })
  }

  function inativar(prestador: Prestador): void {
    inativarPrestador.mutate(prestador.id, {
      onSuccess: () => {
        toast.success(`${prestador.nome} saiu de circulação`, {
          description:
            prestador.bloqueios > 0
              ? 'Os turnos já marcados continuam de pé — cancele um a um se for o caso.'
              : 'Ele deixa de contar como prestador livre.',
        })
      },
      onError: (causa: unknown) => {
        toast.error('Não foi possível inativar', { description: errorMessage(causa) })
      },
    })
  }

  function verEscalaDe(prestador: Prestador): void {
    setPrestadorDaEscala(prestador.id)
    setPaginaDaEscala(1)
    secaoDaEscala.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const colunasDaCapacidade = useMemo<ColumnDef<CapacidadeDaCategoria>[]>(
    () => [
      {
        id: 'categoria',
        header: 'Categoria',
        accessorFn: (linha: CapacidadeDaCategoria) => linha.nome,
        cell: ({ row }) => (
          <p className="truncate font-medium text-foreground">{row.original.nome}</p>
        ),
      },
      {
        id: 'disponiveis',
        header: 'Disponíveis',
        accessorFn: (linha: CapacidadeDaCategoria) => linha.disponiveis,
        cell: ({ row }) => {
          const item: CapacidadeDaCategoria = row.original

          return (
            <div className="flex items-center gap-2">
              <span className="numeric-display text-base text-foreground">
                {disponibilidadeEmTexto(item)}
              </span>
              {item.disponiveis === 0 ? (
                <Badge variant="danger">
                  <CircleSlash aria-hidden="true" />
                  Esgotada
                </Badge>
              ) : null}
            </div>
          )
        },
      },
      {
        id: 'overbooking',
        header: 'Overbooking',
        accessorFn: (linha: CapacidadeDaCategoria) => linha.overbookingDisponivel,
        cell: ({ row }) => {
          const extra = row.original.overbookingDisponivel

          return extra === 0 ? (
            <span className="text-xs text-fg-muted">Desligado</span>
          ) : (
            <span className="tabular-money text-sm text-foreground">
              +{extra} {extra === 1 ? 'unidade' : 'unidades'}
            </span>
          )
        },
      },
    ],
    [],
  )

  const colunasDeBloqueios = useMemo<ColumnDef<BloqueioDeVeiculo>[]>(
    () => [
      {
        id: 'veiculo',
        header: 'Veículo',
        accessorFn: (linha: BloqueioDeVeiculo) => linha.veiculo.placa,
        cell: ({ row }) => {
          const bloqueio: BloqueioDeVeiculo = row.original

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">
                {identificacaoDoVeiculo(bloqueio.veiculo)}
              </p>
              <p className="truncate text-xs text-fg-secondary">{bloqueio.veiculo.marca}</p>
            </div>
          )
        },
      },
      {
        id: 'motivo',
        header: 'Motivo',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeMotivoDeBloqueio motivo={row.original.motivo} />,
      },
      {
        id: 'periodo',
        header: 'Período',
        accessorFn: (linha: BloqueioDeVeiculo) => linha.inicio,
        cell: ({ row }) => {
          const bloqueio: BloqueioDeVeiculo = row.original

          return (
            <div className="min-w-0">
              <p className="whitespace-nowrap text-sm tabular-money text-foreground">
                {formatarDataHora(bloqueio.inicio)} → {formatarDataHora(bloqueio.fim)}
              </p>
              {bloqueio.observacoes === null || bloqueio.observacoes.length === 0 ? null : (
                <p className="truncate text-xs text-fg-secondary">{bloqueio.observacoes}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const bloqueio: BloqueioDeVeiculo = row.original
          const daReserva = MOTIVOS_CONTROLADOS_PELA_RESERVA.includes(bloqueio.motivo)

          // Bloqueio de reserva não se edita nem se libera pelo painel — a API
          // recusa, e oferecer o caminho seria prometer o que não se cumpre.
          if (!podeBloquear || daReserva) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações do bloqueio de ${identificacaoDoVeiculo(bloqueio.veiculo)}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    abrirEdicaoDeBloqueio(bloqueio)
                  }}
                >
                  <Pencil aria-hidden="true" />
                  Editar período
                </DropdownMenuItem>

                <DropdownMenuSeparator />

                <DropdownMenuItem
                  onSelect={() => {
                    liberarVeiculo(bloqueio)
                  }}
                >
                  <LockOpen aria-hidden="true" />
                  Liberar veículo
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // As funções de ação são estáveis dentro do render desta tela; o que muda o
    // conjunto de colunas é a permissão de bloquear.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeBloquear],
  )

  const colunasDePrestadores = useMemo<ColumnDef<Prestador>[]>(
    () => [
      {
        id: 'nome',
        header: 'Prestador',
        accessorFn: (linha: Prestador) => linha.nome,
        cell: ({ row }) => {
          const prestador: Prestador = row.original
          const contato = [prestador.telefone, prestador.email]
            .filter((valor): valor is string => valor !== null && valor.length > 0)
            .join(' · ')

          return (
            <div className="min-w-0">
              <p className="truncate font-medium text-foreground">{prestador.nome}</p>
              {contato.length === 0 ? null : (
                <p className="truncate text-xs tabular-money text-fg-secondary">{contato}</p>
              )}
            </div>
          )
        },
      },
      {
        id: 'tipo',
        header: 'Tipo',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeTipoDePrestador tipo={row.original.tipo} />,
      },
      // ⚠️ A coluna SOME para quem não tem `service_provider.cost`, em vez de
      // mostrar um traço. Um traço numa coluna de dinheiro lê-se como "sem
      // custo cadastrado" — que é uma afirmação sobre o prestador, e falsa. A
      // ausência da coluna é uma afirmação sobre o LEITOR, que é a verdadeira.
      //
      // Esconder aqui não é a defesa: a defesa é a API não mandar o número (ver
      // `visibilidadeDoPrestador`). Isto é só não mostrar coluna vazia.
      ...(podeVerCusto
        ? [
            {
              id: 'custo',
              header: 'Custo por diária',
              // `as const`: dentro do spread condicional o array não recebe o
              // tipo da coluna, e o literal viraria `string`.
              meta: { alinhamento: 'direita' as const },
              accessorFn: (linha: Prestador) => (linha.custoDiario ?? '').padStart(20, '0'),
              cell: ({ row }: { row: { original: Prestador } }) => (
                <span className="money-cell block text-foreground">
                  {row.original.custoDiario === null
                    ? '—'
                    : formatarMoedaDoWire(row.original.custoDiario)}
                </span>
              ),
            },
          ]
        : []),
      {
        id: 'base',
        header: 'Base',
        enableSorting: false,
        cell: ({ row }) => (
          <span className="truncate text-sm text-fg-secondary">
            {nomeDaBase(row.original.locationId, bases)}
          </span>
        ),
      },
      {
        id: 'situacao',
        header: 'Situação',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex flex-col items-start gap-1">
            <BadgeDeSituacaoDoPrestador status={row.original.status} />
            {row.original.bloqueios === 0 ? null : (
              <Badge variant="outline" className="tabular-money">
                {row.original.bloqueios} na escala
              </Badge>
            )}
          </div>
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const prestador: Prestador = row.original

          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Ações de ${prestador.nome}`}
                  className="size-8"
                >
                  <EllipsisVertical aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>

              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={() => {
                    verEscalaDe(prestador)
                  }}
                >
                  <CalendarRange aria-hidden="true" />
                  Ver a escala
                </DropdownMenuItem>

                {podeEditarPrestador ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      setPrestadorEmEdicao(prestador)
                      setPrestadorAberto(true)
                    }}
                  >
                    <Pencil aria-hidden="true" />
                    Editar cadastro
                  </DropdownMenuItem>
                ) : null}

                {podeInativarPrestador && prestador.status === 'ATIVO' ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onSelect={() => {
                        inativar(prestador)
                      }}
                    >
                      <CircleSlash aria-hidden="true" />
                      Tirar de circulação
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          )
        },
      },
    ],
    // Além das permissões, o mapa de bases muda o texto de uma coluna — sem ele
    // na lista, a base ficaria como traço até a próxima mudança de filtro.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeEditarPrestador, podeInativarPrestador, podeVerCusto, bases],
  )

  const colunasDaEscala = useMemo<ColumnDef<BloqueioDePrestador>[]>(
    () => [
      {
        id: 'prestador',
        header: 'Prestador',
        accessorFn: (linha: BloqueioDePrestador) => linha.prestador.nome,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{row.original.prestador.nome}</p>
            <p className="truncate text-xs text-fg-secondary">
              {ROTULO_DO_TIPO_DE_PRESTADOR[row.original.prestador.tipo]}
            </p>
          </div>
        ),
      },
      {
        id: 'motivo',
        header: 'Motivo',
        enableSorting: false,
        cell: ({ row }) => <BadgeDeMotivoDeBloqueio motivo={row.original.motivo} />,
      },
      {
        id: 'periodo',
        header: 'Período',
        accessorFn: (linha: BloqueioDePrestador) => linha.inicio,
        cell: ({ row }) => (
          <div className="min-w-0">
            <p className="whitespace-nowrap text-sm tabular-money text-foreground">
              {formatarDataHora(row.original.inicio)} → {formatarDataHora(row.original.fim)}
            </p>
            {row.original.observacoes === null || row.original.observacoes.length === 0 ? null : (
              <p className="truncate text-xs text-fg-secondary">{row.original.observacoes}</p>
            )}
          </div>
        ),
      },
      {
        id: 'acoes',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const turno: BloqueioDePrestador = row.original

          // Não há `PATCH` de escala: mover o turno de uma pessoa exige avaliar
          // o intervalo novo inteiro. Quem errou o horário libera e marca de novo.
          if (!podeBloquear || MOTIVOS_CONTROLADOS_PELA_RESERVA.includes(turno.motivo)) {
            return <span className="text-xs text-fg-muted">{SEM_VALOR}</span>
          }

          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                liberarTurno(turno)
              }}
            >
              <LockOpen aria-hidden="true" />
              Liberar
            </Button>
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [podeBloquear],
  )

  const dadosDeBloqueios = bloqueios.data
  const dadosDePrestadores = prestadores.data
  const dadosDaEscala = escala.data
  // Com a janela inválida a consulta está desligada e `capacidade.data` ainda
  // guarda a resposta anterior. Zerar aqui é o que impede a tela de afirmar um
  // número para um período que não está mais preenchido.
  const janela = janelaValida ? capacidade.data : undefined
  const livresNoPeriodo = janelaValida ? (prestadoresLivres.data?.itens ?? []) : []

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight">Disponibilidade</h1>
          <p className="max-w-prose text-sm text-fg-secondary">
            Quantos carros de cada categoria estão livres no período, quais estão parados
            (manutenção, limpeza, vistoria) e quais motoristas e seguranças estão disponíveis.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {podeVerPolitica ? (
            <Button
              variant="secondary"
              onClick={() => {
                setPoliticaAberta(true)
              }}
            >
              <SlidersHorizontal aria-hidden="true" />
              Política de locação
            </Button>
          ) : null}

          {podeBloquear ? (
            <Button onClick={abrirBloqueioDeVeiculo}>
              <Plus aria-hidden="true" />
              Bloquear veículo
            </Button>
          ) : null}
        </div>
      </div>

      {/* Categorias e locais alimentam TODOS os seletores desta tela. Sem este
          aviso, a falha aparecia como um seletor vazio — que se lê como "não há
          categoria cadastrada", e manda a operação cadastrar o que já existe. */}
      {categorias.isError || locais.isError ? (
        <Alert variant="warning">
          <AlertTitle>Não foi possível carregar os filtros</AlertTitle>
          <AlertDescription>
            {categorias.isError ? errorMessage(categorias.error) : errorMessage(locais.error)} Os
            seletores de categoria e de ponto de retirada ficam vazios até a lista voltar.
          </AlertDescription>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="mt-2"
            onClick={() => {
              if (categorias.isError) {
                void categorias.refetch()
              }

              if (locais.isError) {
                void locais.refetch()
              }
            }}
          >
            <RefreshCw aria-hidden="true" />
            Tentar de novo
          </Button>
        </Alert>
      ) : null}

      {/* ------------------------------------------------------------------
          A janela: capacidade por categoria e gente livre
          ------------------------------------------------------------------ */}
      <section aria-labelledby="titulo-da-janela" className="flex flex-col gap-4">
        <div>
          <h2 id="titulo-da-janela" className="text-base font-semibold tracking-tight">
            Capacidade no período
          </h2>
          <p className="text-sm text-fg-secondary">
            A contagem é por categoria. A placa só é decidida na alocação.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="janela-retirada">Retirada</Label>
            <Input
              id="janela-retirada"
              type="datetime-local"
              value={retiradaDigitada}
              onChange={(evento) => {
                setRetiradaDigitada(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="janela-devolucao">Devolução</Label>
            <Input
              id="janela-devolucao"
              type="datetime-local"
              value={devolucaoDigitada}
              onChange={(evento) => {
                setDevolucaoDigitada(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="janela-categoria">Categoria</Label>
            <Select
              id="janela-categoria"
              value={categoriaDaJanela}
              onChange={(evento) => {
                setCategoriaDaJanela(evento.target.value)
              }}
              disabled={categorias.isPending}
            >
              <option value="">{categorias.isPending ? 'Carregando…' : 'Todas'}</option>
              {listaDeCategorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nome}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="janela-local">Ponto de retirada</Label>
            <Select
              id="janela-local"
              value={localDaJanela}
              onChange={(evento) => {
                setLocalDaJanela(evento.target.value)
              }}
              disabled={locais.isPending}
            >
              <option value="">{locais.isPending ? 'Carregando…' : 'Qualquer ponto'}</option>
              {listaDeLocais.map((local) => (
                <option key={local.id} value={local.id}>
                  {local.nome}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {avisoDaJanela === null ? null : (
          <Alert variant="warning">
            <AlertDescription>{avisoDaJanela}</AlertDescription>
          </Alert>
        )}

        {/* A janela consultada é MAIOR que o período pedido, e dizer isso em voz
            alta evita o chamado "o carro devolve às 10h, por que não posso
            alugar às 10h05?". */}
        {janela === undefined ? null : (
          <p className="text-xs tabular-money text-fg-muted">
            Com o buffer de {janela.bufferEmHoras} h de limpeza e vistoria, a consulta cobre de{' '}
            {formatarDataHora(janela.janelaDe)} a {formatarDataHora(janela.janelaAte)}.
          </p>
        )}

        {/* O primeiro carregamento é esqueleto; do segundo em diante a contagem
            anterior continua na tela enquanto a nova chega, e esta linha é o que
            avisa que o número ainda vai mudar. */}
        {janelaValida && capacidade.isFetching && janela !== undefined ? (
          <p role="status" className="text-xs text-fg-muted">
            Atualizando a contagem do período…
          </p>
        ) : null}

        <TabelaDeDados
          colunas={colunasDaCapacidade}
          dados={janela?.itens ?? []}
          carregando={capacidade.isPending && janelaValida}
          erro={capacidade.isError ? capacidade.error : undefined}
          aoTentarNovamente={() => {
            void capacidade.refetch()
          }}
          legenda="Capacidade por categoria no período"
          estadoVazio={
            <EmptyState
              icone={CarFront}
              titulo={janelaValida ? 'Nenhuma categoria neste recorte' : 'Informe o período'}
              descricao={
                janelaValida
                  ? 'Nenhuma categoria ativa atende o filtro escolhido. Tire a categoria ou o ponto de retirada para ver a frota inteira.'
                  : 'Retirada e devolução definem a pergunta. Sem as duas pontas não há disponibilidade a calcular.'
              }
            />
          }
        />

        {/* Prestadores livres — a leitura que decide se a cotação pode oferecer
            motorista e segurança como extra. */}
        {podeVerPrestadores ? (
          <div className="grid gap-3 sm:grid-cols-2">
            {!janelaValida ? (
              <p className="text-sm text-fg-secondary sm:col-span-2">
                Informe o período acima para saber quantos motoristas e seguranças ficam livres.
              </p>
            ) : prestadoresLivres.isPending ? (
              <>
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-20 w-full" />
              </>
            ) : prestadoresLivres.isError ? (
              <Alert variant="danger" className="sm:col-span-2">
                <AlertTitle>Não foi possível contar os prestadores livres</AlertTitle>
                <AlertDescription>{errorMessage(prestadoresLivres.error)}</AlertDescription>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    void prestadoresLivres.refetch()
                  }}
                >
                  <RefreshCw aria-hidden="true" />
                  Tentar de novo
                </Button>
              </Alert>
            ) : livresNoPeriodo.length === 0 ? (
              <p className="text-sm text-fg-secondary sm:col-span-2">
                Nenhum motorista e nenhum segurança cadastrados — a cotação recusa esses extras por
                falta de prestador.
              </p>
            ) : (
              livresNoPeriodo.map((item) => {
                const Icone = ICONE_DO_TIPO_DE_PRESTADOR[item.tipo]

                return (
                  <div
                    key={item.tipo}
                    className="flex items-start justify-between gap-3 rounded-lg border border-border bg-surface p-4"
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        {ROTULO_DO_TIPO_DE_PRESTADOR[item.tipo]}
                      </p>
                      <p className="numeric-display mt-1 text-xl text-foreground">
                        {disponibilidadeEmTexto({
                          disponiveis: item.livres,
                          capacidade: item.total,
                        })}
                      </p>
                      <p className="mt-1 text-xs text-fg-muted">
                        {item.total === 0
                          ? 'Nenhum cadastrado — o extra não pode ser vendido.'
                          : item.livres === 0
                            ? 'Todos ocupados no período. O extra é recusado na cotação.'
                            : 'Livres no período inteiro.'}
                      </p>
                    </div>
                    <Icone aria-hidden="true" className="size-4 text-fg-muted" />
                  </div>
                )
              })
            )}
          </div>
        ) : null}
      </section>

      <Separator />

      {/* ------------------------------------------------------------------
          Calendário dia a dia
          ------------------------------------------------------------------ */}
      <section aria-labelledby="titulo-do-calendario" className="flex flex-col gap-4">
        <div>
          <h2 id="titulo-do-calendario" className="text-base font-semibold tracking-tight">
            Calendário da categoria
          </h2>
          <p className="text-sm text-fg-secondary">
            Quantas unidades sobram em cada dia. Aqui o buffer não entra: o cliente escolhe dia, não
            hora — e o preço, este mapa não diz, porque quem precifica é o motor.
          </p>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5">
            <Label htmlFor="calendario-categoria">Categoria</Label>
            <Select
              id="calendario-categoria"
              value={categoriaDoCalendario}
              onChange={(evento) => {
                setCategoriaDoCalendario(evento.target.value)
              }}
              disabled={categorias.isPending}
            >
              <option value="">
                {categorias.isPending ? 'Carregando…' : 'Selecione a categoria'}
              </option>
              {listaDeCategorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nome}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="calendario-de">De</Label>
            <Input
              id="calendario-de"
              type="date"
              value={deDoCalendario}
              onChange={(evento) => {
                setDeDoCalendario(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="calendario-ate">Até</Label>
            <Input
              id="calendario-ate"
              type="date"
              value={ateDoCalendario}
              onChange={(evento) => {
                setAteDoCalendario(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>
        </div>

        {erroDoCalendario === null ? null : (
          <Alert variant="warning">
            <AlertDescription>{erroDoCalendario}</AlertDescription>
          </Alert>
        )}

        {categoriaDoCalendario === '' ? (
          <EmptyState
            icone={CalendarDays}
            titulo="Escolha uma categoria"
            descricao="O calendário responde por uma classe de cada vez — é assim que o cliente compra."
          />
        ) : erroDoCalendario !== null ? (
          /* A consulta fica desligada com a janela fora do limite, e `isPending`
             de query desligada é `true` para sempre: sem este ramo, a tela
             mostraria esqueleto eterno em vez do que há para corrigir. */
          <EmptyState icone={CalendarDays} titulo="Ajuste a janela" descricao={erroDoCalendario} />
        ) : calendario.isPending ? (
          <LoadingState rotulo="Carregando o calendário…" linhas={2} />
        ) : calendario.isError ? (
          <ErrorState
            titulo="Não foi possível carregar o calendário"
            erro={calendario.error}
            aoTentarNovamente={() => {
              void calendario.refetch()
            }}
          />
        ) : calendario.data.dias.length === 0 ? (
          <EmptyState
            icone={CalendarDays}
            titulo="Nenhum dia nesta janela"
            descricao="Ajuste as datas para ver a disponibilidade."
          />
        ) : (
          <ul className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-7">
            {calendario.data.dias.map((dia) => (
              <CartaoDoDia
                key={dia.data}
                data={dia.data}
                capacidade={dia.capacidade}
                disponiveis={dia.disponiveis}
              />
            ))}
          </ul>
        )}
      </section>

      <Separator />

      {/* ------------------------------------------------------------------
          Bloqueios de veículo
          ------------------------------------------------------------------ */}
      <section aria-labelledby="titulo-dos-bloqueios" className="flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 id="titulo-dos-bloqueios" className="text-base font-semibold tracking-tight">
              Bloqueios de veículo
            </h2>
            <p className="max-w-prose text-sm text-fg-secondary">
              Manutenção, limpeza, vistoria e sinistro entram aqui. Alocação e reserva aparecem, mas
              são escritas pela reserva — não se editam por esta tela.
            </p>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="filtro-categoria-bloqueio">Categoria</Label>
            <Select
              id="filtro-categoria-bloqueio"
              value={categoriaDoBloqueio}
              onChange={(evento) => {
                setPaginaDeBloqueios(1)
                setCategoriaDoBloqueio(evento.target.value)
              }}
              disabled={categorias.isPending}
            >
              <option value="">Todas</option>
              {listaDeCategorias.map((categoria) => (
                <option key={categoria.id} value={categoria.id}>
                  {categoria.nome}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-motivo-bloqueio">Motivo</Label>
            <Select
              id="filtro-motivo-bloqueio"
              value={motivoDoBloqueio}
              onChange={(evento) => {
                setPaginaDeBloqueios(1)
                const valor = evento.target.value
                setMotivoDoBloqueio(MOTIVOS_DE_BLOQUEIO.find((item) => item === valor) ?? '')
              }}
            >
              <option value="">Todos</option>
              {MOTIVOS_DE_BLOQUEIO.map((motivo) => (
                <option key={motivo} value={motivo}>
                  {ROTULO_DO_MOTIVO_DE_BLOQUEIO[motivo]}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-de-bloqueio">Colide a partir de</Label>
            <Input
              id="filtro-de-bloqueio"
              type="date"
              value={deDoBloqueio}
              onChange={(evento) => {
                setPaginaDeBloqueios(1)
                setDeDoBloqueio(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="filtro-ate-bloqueio">Colide até</Label>
            <Input
              id="filtro-ate-bloqueio"
              type="date"
              value={ateDoBloqueio}
              onChange={(evento) => {
                setPaginaDeBloqueios(1)
                setAteDoBloqueio(evento.target.value)
              }}
              className="tabular-money"
            />
          </div>
        </div>

        <TabelaDeDados
          colunas={colunasDeBloqueios}
          dados={dadosDeBloqueios?.itens ?? []}
          carregando={bloqueios.isPending}
          erro={bloqueios.isError ? bloqueios.error : undefined}
          aoTentarNovamente={() => {
            void bloqueios.refetch()
          }}
          legenda="Bloqueios de veículo"
          estadoVazio={
            <EmptyState
              icone={CalendarRange}
              titulo={
                temFiltroDeBloqueio ? 'Nenhum bloqueio com esses filtros' : 'Nenhum bloqueio ativo'
              }
              descricao={
                temFiltroDeBloqueio
                  ? 'Ajuste a categoria, o motivo ou o recorte de período.'
                  : 'A frota inteira está disponível. Bloqueie um carro quando ele sair de circulação para manutenção, limpeza ou vistoria.'
              }
              acao={
                temFiltroDeBloqueio ? (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setCategoriaDoBloqueio('')
                      setMotivoDoBloqueio('')
                      setDeDoBloqueio('')
                      setAteDoBloqueio('')
                      setPaginaDeBloqueios(1)
                    }}
                  >
                    Limpar filtros
                  </Button>
                ) : podeBloquear ? (
                  <Button variant="secondary" onClick={abrirBloqueioDeVeiculo}>
                    <Plus aria-hidden="true" />
                    Bloquear veículo
                  </Button>
                ) : undefined
              }
            />
          }
        />

        <Paginacao
          pagina={dadosDeBloqueios?.pagina ?? paginaDeBloqueios}
          tamanho={dadosDeBloqueios?.tamanho ?? TAMANHO_DA_PAGINA}
          total={dadosDeBloqueios?.total ?? 0}
          aoMudarDePagina={setPaginaDeBloqueios}
          desabilitado={bloqueios.isFetching}
        />
      </section>

      {podeVerPrestadores ? (
        <>
          <Separator />

          {/* ----------------------------------------------------------------
              Prestadores e escala
              ---------------------------------------------------------------- */}
          <section aria-labelledby="titulo-dos-prestadores" className="flex flex-col gap-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 id="titulo-dos-prestadores" className="text-base font-semibold tracking-tight">
                  Motoristas e seguranças
                </h2>
                <p className="max-w-prose text-sm text-fg-secondary">
                  O custo aqui é o que a operação PAGA por diária. O preço ao cliente é do extra — a
                  distância entre os dois é a margem do serviço.
                </p>
              </div>

              {podeCriarPrestador ? (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setPrestadorEmEdicao(undefined)
                    setPrestadorAberto(true)
                  }}
                >
                  <Users aria-hidden="true" />
                  Novo prestador
                </Button>
              ) : null}
            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-1.5">
                <Label htmlFor="filtro-tipo-prestador">Tipo</Label>
                <Select
                  id="filtro-tipo-prestador"
                  value={tipoDoPrestador}
                  onChange={(evento) => {
                    setPaginaDePrestadores(1)
                    const valor = evento.target.value
                    setTipoDoPrestador(TIPOS_DE_PRESTADOR.find((item) => item === valor) ?? '')
                  }}
                >
                  <option value="">Todos</option>
                  {TIPOS_DE_PRESTADOR.map((tipo) => (
                    <option key={tipo} value={tipo}>
                      {ROTULO_DO_TIPO_DE_PRESTADOR[tipo]}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="filtro-situacao-prestador">Situação</Label>
                <Select
                  id="filtro-situacao-prestador"
                  value={situacaoDoPrestador}
                  onChange={(evento) => {
                    setPaginaDePrestadores(1)
                    const valor = evento.target.value
                    setSituacaoDoPrestador(STATUS_DE_PRESTADOR.find((item) => item === valor) ?? '')
                  }}
                >
                  <option value="">Todas</option>
                  {STATUS_DE_PRESTADOR.map((status) => (
                    <option key={status} value={status}>
                      {ROTULO_DO_STATUS_DE_PRESTADOR[status]}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            <TabelaDeDados
              colunas={colunasDePrestadores}
              dados={dadosDePrestadores?.itens ?? []}
              carregando={prestadores.isPending}
              erro={prestadores.isError ? prestadores.error : undefined}
              aoTentarNovamente={() => {
                void prestadores.refetch()
              }}
              legenda="Prestadores de serviço"
              estadoVazio={
                <EmptyState
                  icone={Users}
                  titulo={
                    temFiltroDePrestador
                      ? 'Nenhum prestador com esses filtros'
                      : 'Nenhum prestador cadastrado'
                  }
                  descricao={
                    temFiltroDePrestador
                      ? 'Ajuste o tipo ou a situação.'
                      : 'Sem motorista e sem segurança cadastrados, a cotação recusa esses extras por falta de prestador.'
                  }
                  acao={
                    temFiltroDePrestador ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setTipoDoPrestador('')
                          setSituacaoDoPrestador('')
                          setPaginaDePrestadores(1)
                        }}
                      >
                        Limpar filtros
                      </Button>
                    ) : podeCriarPrestador ? (
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setPrestadorEmEdicao(undefined)
                          setPrestadorAberto(true)
                        }}
                      >
                        <Users aria-hidden="true" />
                        Novo prestador
                      </Button>
                    ) : undefined
                  }
                />
              }
            />

            <Paginacao
              pagina={dadosDePrestadores?.pagina ?? paginaDePrestadores}
              tamanho={dadosDePrestadores?.tamanho ?? TAMANHO_DA_PAGINA}
              total={dadosDePrestadores?.total ?? 0}
              aoMudarDePagina={setPaginaDePrestadores}
              desabilitado={prestadores.isFetching}
            />
          </section>

          <section
            ref={secaoDaEscala}
            aria-labelledby="titulo-da-escala"
            className="flex flex-col gap-4"
          >
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div className="min-w-0">
                <h2 id="titulo-da-escala" className="text-base font-semibold tracking-tight">
                  Escala
                </h2>
                <p className="text-sm text-fg-secondary">
                  Os períodos em que cada pessoa está ocupada. Não há edição de turno: quem errou o
                  horário libera e marca de novo.
                </p>
              </div>

              <div className="flex w-full flex-wrap items-end gap-2 sm:w-auto">
                <div className="min-w-44 flex-1 space-y-1.5 sm:flex-none">
                  <Label htmlFor="filtro-prestador-escala">Prestador</Label>
                  <Select
                    id="filtro-prestador-escala"
                    value={prestadorDaEscala}
                    onChange={(evento) => {
                      setPaginaDaEscala(1)
                      setPrestadorDaEscala(evento.target.value)
                    }}
                    disabled={prestadores.isPending}
                  >
                    <option value="">Todos</option>
                    {(dadosDePrestadores?.itens ?? []).map((prestador) => (
                      <option key={prestador.id} value={prestador.id}>
                        {prestador.nome}
                      </option>
                    ))}
                  </Select>
                </div>

                {podeBloquear ? (
                  <Button variant="secondary" onClick={abrirTurnoDePrestador}>
                    <CalendarRange aria-hidden="true" />
                    Marcar turno
                  </Button>
                ) : null}
              </div>
            </div>

            <TabelaDeDados
              colunas={colunasDaEscala}
              dados={dadosDaEscala?.itens ?? []}
              carregando={escala.isPending}
              erro={escala.isError ? escala.error : undefined}
              aoTentarNovamente={() => {
                void escala.refetch()
              }}
              legenda="Escala dos prestadores"
              estadoVazio={
                <EmptyState
                  icone={CalendarRange}
                  titulo="Nenhum turno marcado"
                  descricao="Todo mundo está livre. Marque um turno quando a pessoa estiver comprometida em outro serviço."
                  acao={
                    podeBloquear ? (
                      <Button variant="secondary" onClick={abrirTurnoDePrestador}>
                        <CalendarRange aria-hidden="true" />
                        Marcar turno
                      </Button>
                    ) : undefined
                  }
                />
              }
            />

            <Paginacao
              pagina={dadosDaEscala?.pagina ?? paginaDaEscala}
              tamanho={dadosDaEscala?.tamanho ?? TAMANHO_DA_PAGINA}
              total={dadosDaEscala?.total ?? 0}
              aoMudarDePagina={setPaginaDaEscala}
              desabilitado={escala.isFetching}
            />
          </section>
        </>
      ) : null}

      {podeVerPolitica ? <ResumoDaPolitica /> : null}

      {podeBloquear ? (
        <DialogoDeBloqueio
          aberto={bloqueioAberto}
          alvo={alvoDoDialogo}
          bloqueio={bloqueioEmEdicao}
          aoFechar={() => {
            setBloqueioAberto(false)
          }}
        />
      ) : null}

      {podeCriarPrestador || podeEditarPrestador ? (
        <DialogoDePrestador
          aberto={prestadorAberto}
          prestador={prestadorEmEdicao}
          aoFechar={() => {
            setPrestadorAberto(false)
          }}
        />
      ) : null}

      {podeVerPolitica ? (
        <DialogoDePoliticaDeLocacao
          aberto={politicaAberta}
          podeEditar={podeEditarPolitica}
          aoFechar={() => {
            setPoliticaAberta(false)
          }}
        />
      ) : null}
    </div>
  )
}
